'use client';

/**
 * The consent behind the inbox inside an app (2026-09-24, Revent's in-app
 * inbox; lib/workspaces/widget-ops/agent-inbox.ts). Portal chrome rendered
 * OVER the app, never inside its frame, twin of {@link WidgetAppEmail}: an
 * app is untrusted code, so the two things that act for the person in the
 * world are confirmed here and nowhere else.
 *
 *   decide   accept, decline or block a sender who wrote to the person's agent
 *   send     send a reply (the agent's draft or the person's own), showing
 *            the EXACT text that goes
 *   letter   send a FIRST email from the person's own app agent's address to
 *            a listed recipient (ADR 0050, 2026-10-04): To, From (name and
 *            address from the platform's read, never from the app), the
 *            subject, where the answer comes, and the whole text as it will
 *            be sent. This dialog is the consent: no `Auto-Submitted` and no
 *            AI line go on that email because the person read exactly this
 *            and pressed Send.
 *
 * Opened via a window CustomEvent('widget-inbox-consent') whose detail is one
 * of the requests below plus `resolve`. Every name and every line of text in
 * it comes from the platform's own reads, which the bridge makes before it
 * opens this (the item, the draft, the From of a first email), never from
 * what the app said; a letter's To, subject and text ARE the app's words,
 * which is the point: they are shown so the person approves exactly them. A
 * sender's name, a subject and a draft are still other people's or a
 * model's words, so they are shown as quoted text, never as the platform
 * speaking. The bridge awaits `resolve(true)` on confirm and
 * `resolve(false)` on "Not now", Escape or the backdrop.
 *
 * Mounted on the canvas (workspace-shell.tsx) AND on the full-screen app page
 * (components/widgets/standalone-widget.tsx): a notice opens Revent at `/w/`,
 * and a dialog nobody listens for leaves the bridge waiting forever.
 */

import { useEffect, useState } from 'react';
import { Ban, Mail, Send, UserCheck, UserX } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { useAppFrameFocusReturn } from '@/components/workspace/app-frame-focus-return';
import { cn } from '@/lib/utils';

export type InboxConsentRequest =
  | {
      kind: 'decide';
      action: 'accept' | 'decline' | 'block';
      appName: string;
      agentName: string;
      who: string;
      verified: boolean;
      subject: string | null;
    }
  | {
      kind: 'send';
      appName: string;
      agentName: string;
      who: string;
      transport: 'portal' | 'smtp';
      text: string;
    }
  | {
      kind: 'letter';
      appName: string;
      agentName: string;
      to: string;
      fromName: string;
      fromAddress: string;
      subject: string;
      text: string;
    };

type Pending = InboxConsentRequest & { resolve: (confirmed: boolean) => void };

export const INBOX_CONSENT_EVENT = 'widget-inbox-consent';

export function WidgetInboxConsent() {
  const t = useT();
  const [req, setReq] = useState<Pending | null>(null);
  // Close hands focus back to the app that asked (app-frame-focus-return.ts).
  const captureAppFocus = useAppFrameFocusReturn(!!req);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<Pending>).detail;
      if (!detail || typeof detail.resolve !== 'function') return;
      captureAppFocus();
      // One dialog at a time: a second ask while one is open is a "not now".
      setReq((open) => {
        if (open) { detail.resolve(false); return open; }
        return detail;
      });
    };
    window.addEventListener(INBOX_CONSENT_EVENT, onOpen);
    return () => window.removeEventListener(INBOX_CONSENT_EVENT, onOpen);
  }, [captureAppFocus]);

  useEffect(() => {
    if (!req) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { req.resolve(false); setReq(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req]);

  if (!req) return null;

  const finish = (confirmed: boolean) => {
    req.resolve(confirmed);
    setReq(null);
  };

  let Icon = UserCheck;
  let eyebrow = '';
  let title = '';
  let confirm = '';
  let danger = false;
  const lines: string[] = [];
  let quoted: string | null = null;

  if (req.kind === 'decide') {
    eyebrow = t('widgetInboxConsent.contactEyebrow');
    if (req.action === 'accept') {
      Icon = UserCheck;
      title = t('widgetInboxConsent.acceptTitle', { who: req.who });
      lines.push(t('widgetInboxConsent.acceptBody', { who: req.who, agent: req.agentName }));
      confirm = t('widgetInboxConsent.acceptConfirm');
    } else if (req.action === 'decline') {
      Icon = UserX;
      title = t('widgetInboxConsent.declineTitle', { who: req.who });
      lines.push(t('widgetInboxConsent.declineBody'));
      confirm = t('widgetInboxConsent.declineConfirm');
    } else {
      Icon = Ban;
      danger = true;
      title = t('widgetInboxConsent.blockTitle', { who: req.who });
      lines.push(t('widgetInboxConsent.blockBody'));
      confirm = t('widgetInboxConsent.blockConfirm');
    }
    if (!req.verified) lines.push(t('widgetInboxConsent.unverified'));
    if (req.subject) quoted = req.subject;
  } else if (req.kind === 'letter') {
    Icon = Mail;
    eyebrow = t('widgetInboxConsent.letterEyebrow');
    title = t('widgetInboxConsent.letterTitle', { to: req.to });
    lines.push(t('widgetInboxConsent.letterFrom', { name: req.fromName, address: req.fromAddress }));
    lines.push(t('widgetInboxConsent.letterSubject', { subject: req.subject }));
    lines.push(t('widgetInboxConsent.letterAnswers', { app: req.appName }));
    quoted = req.text;
    confirm = t('widgetInboxConsent.letterConfirm');
  } else {
    Icon = Send;
    eyebrow = t('widgetInboxConsent.sendEyebrow');
    title = t('widgetInboxConsent.sendTitle', { who: req.who });
    lines.push(req.transport === 'smtp'
      ? t('widgetInboxConsent.sendByEmail', { agent: req.agentName })
      : t('widgetInboxConsent.sendInChat', { agent: req.agentName }));
    quoted = req.text;
    confirm = t('widgetInboxConsent.sendConfirm');
  }

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={t('widgetInboxConsent.ariaLabel', { app: req.appName })}
      // The panel's place comes from the classes below (a bottom sheet on a
      // phone, a card near the top on a desktop). The alignment was inline
      // too, and an inline style beats `md:items-start`, so on a desktop the
      // dialog sat at the bottom of the page (components/sharing/
      // share-dialog.tsx has the finding, 2026-10-02).
      style={{ position: 'fixed', inset: 0, zIndex: 240, display: 'flex', justifyContent: 'center' }}
      className="items-end bg-black/40 backdrop-blur-sm md:items-start md:py-[14vh]"
    >
      <div aria-hidden className="absolute inset-0" onClick={() => finish(false)} />
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'glass-panel relative flex w-full flex-col overflow-hidden shadow-2xl',
          'max-h-[90vh] rounded-t-2xl',
          'md:w-[min(460px,96vw)] md:rounded-2xl',
        )}
      >
        <div className="min-h-0 overflow-y-auto px-4 py-3">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Icon className="h-3 w-3" /> {eyebrow}
          </div>
          <div className="mt-1 break-words text-sm font-semibold tracking-tight">{title}</div>
          {lines.map((line) => (
            <p key={line} className="mt-1.5 text-xs text-muted-foreground">{line}</p>
          ))}
          {quoted && (
            <blockquote
              className={cn(
                'mt-2.5 whitespace-pre-wrap break-words rounded-lg bg-muted/50 px-3 py-2 text-sm',
                req.kind === 'send' || req.kind === 'letter' ? 'max-h-[40vh] overflow-y-auto' : 'text-muted-foreground',
              )}
            >
              {quoted}
            </blockquote>
          )}
        </div>
        <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.35)]">
          {/* FIRST FOCUS ON THE SAFE CONTROL (the dev visual pass of
              2026-10-09, finding 6): the dialog opened with focus on Send,
              so a stray Enter sent an email, a reply or a letter. Not now
              takes first focus; Enter closes the dialog and sends nothing,
              and Send is one Tab away. */}
          <button
            type="button"
            autoFocus
            onClick={() => finish(false)}
            className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50"
          >
            {t('widgetInboxConsent.cancel')}
          </button>
          <button
            type="button"
            onClick={() => finish(true)}
            className={cn(
              'rounded-lg px-3.5 py-1.5 text-sm font-medium',
              danger ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground',
            )}
          >
            {confirm}
          </button>
        </div>
      </div>
    </PopoverPortal>
  );
}
